import { useRef, useState, type PointerEvent, type ReactNode } from 'react';
import './SwipeRow.css';

interface Props {
  children: ReactNode;
  onComplete?: () => void;
  onDelete?: () => void;
}

/** Past this much horizontal travel, releasing commits the action. */
const THRESHOLD = 76;
/** Below this, the gesture is treated as a vertical scroll and ignored. */
const SLOP = 8;

/**
 * Swipe right to complete, swipe left to delete — both land in an undo toast,
 * which is what makes a one-handed destructive gesture safe enough to use.
 */
export function SwipeRow({ children, onComplete, onDelete }: Props) {
  const [dx, setDx] = useState(0);
  const start = useRef<{ x: number; y: number } | null>(null);
  const axis = useRef<'none' | 'x' | 'y'>('none');

  function down(e: PointerEvent<HTMLDivElement>) {
    if (e.pointerType === 'mouse') return;
    start.current = { x: e.clientX, y: e.clientY };
    axis.current = 'none';
  }

  function move(e: PointerEvent<HTMLDivElement>) {
    if (!start.current) return;
    const deltaX = e.clientX - start.current.x;
    const deltaY = e.clientY - start.current.y;

    if (axis.current === 'none') {
      if (Math.abs(deltaX) < SLOP && Math.abs(deltaY) < SLOP) return;
      // Lock to whichever axis moved first, so the list still scrolls normally.
      axis.current = Math.abs(deltaX) > Math.abs(deltaY) ? 'x' : 'y';
      if (axis.current === 'x') e.currentTarget.setPointerCapture(e.pointerId);
    }
    if (axis.current !== 'x') return;

    const allowed = deltaX > 0 ? onComplete : onDelete;
    setDx(allowed ? deltaX : 0);
  }

  function up() {
    if (dx > THRESHOLD) onComplete?.();
    else if (dx < -THRESHOLD) onDelete?.();
    setDx(0);
    start.current = null;
    axis.current = 'none';
  }

  const armed = Math.abs(dx) > THRESHOLD;

  return (
    <div
      className={`swipe${dx > 0 ? ' is-completing' : dx < 0 ? ' is-deleting' : ''}${armed ? ' is-armed' : ''}`}
      onPointerDown={down}
      onPointerMove={move}
      onPointerUp={up}
      onPointerCancel={up}
    >
      <span className="swipe__action swipe__action--complete" aria-hidden="true">
        ✓
      </span>
      <span className="swipe__action swipe__action--delete" aria-hidden="true">
        ⌫
      </span>
      <div
        className="swipe__content"
        style={{ transform: `translateX(${dx}px)`, transition: dx === 0 ? 'transform 140ms ease-out' : 'none' }}
      >
        {children}
      </div>
    </div>
  );
}
